import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import {
    IBasicInfoResponseType, IRequestType, IResponseType, IJobInfoResponseType,
    IContactInfoResponseType,
    IEmployeeListRequest,
    IEmployeeListResponse,
    ISecurityInfoResponseType,
    IUpdateEmployeeStatustype,
    IGetEmployeePasswordRequestType,
    IDocumentInfoResponseType,
    IRemoveDoctype,
    IGetEmployeeResponseType,
    IEmailExistingParams,
    IEmployeeCodeParams,
    IDownloadContractParamsType,
    IDownloadContractResponseType,
    IResponseTypes,
    IPaginationResponseTypes,
    ISlackResponseType,
    ISlackRequestType,
    INotificationListRequestType,
    INotificationResponseType,
    IUpdateNotificationStatusRequestType,
    IExitNotesResponseType,
    IBankInfoResponseType,
    IBankInfoRequestType,
    ITANInfoResponseType
} from "./type";
import { handlingErrorResponse } from "utils/HandlingErrorResponse";
import { IContactInfoType, IJobInfoType, ISecurityInfoType, IBankInfoType, IUpdateContractStatusInfo, ITANInfoType } from "../onboardEmployee/type";
import { RootState } from "store/store";
import { getAuthToken } from "utils/getAuthToken";
import { baseUrl } from "constant";
import {
    checkEmailExistingUrl,
    checkEmployeeCodeExistingUrl,
    deleteEmployeeByIdUrl, downloadContractUrl, EmployeeEndpointConst, getBankInfoByIdUrl, getBasicInfoByIdUrl,
    getContactInfoByIdUrl, getDocumentInfoByIdUrl, getEmployeeByIdUrl, getEmployeePasswordUrl,
    getExitNotesUrl,
    getIncapacitateStatusByIdUrl,
    getJobInfoByIdUrl, getSecurityInfoByIdUrl, getSlackSyncUrl, getTANInfoByIdUrl, updateEmployeeStatusUrl, updateNotificationUrl
} from "./const";
import { IExitEmployeeDataRow, IExitEmployeeInfo, IExitemployeeProcessInfo } from "../type";

export const EmployeeApi = createApi({
    reducerPath: 'employeeApi',
    baseQuery: fetchBaseQuery({
        baseUrl: baseUrl,
        prepareHeaders: (headers, { getState }) => {
            const token = getAuthToken(getState() as RootState)
            if (token) {
                headers.set('Authorization', `Bearer ${token}`)
            }
            return headers
        },
    }),
    tagTypes: ['Employee'],
    endpoints: (builder) => ({
        EmployeeList: builder.mutation<IEmployeeListResponse, IEmployeeListRequest>({
            query: userReq => ({
                url: EmployeeEndpointConst.employeeList,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'EmployeeList' }],
        }),
        GetEmployeeDetails: builder.query<IGetEmployeeResponseType, IRequestType>({
            query: (params) => getEmployeeByIdUrl(params),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'EmployeeList' }]
        }),
        DeleteEmployee: builder.mutation<IResponseType, IRequestType>({
            query: params => ({
                url: deleteEmployeeByIdUrl(params),
                method: 'DELETE'
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'EmployeeList' }],
        }),
        GetBasicInfo: builder.query<IBasicInfoResponseType, IRequestType>({
            query: (employeeParams) => getBasicInfoByIdUrl(employeeParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'BasicInfo' }]
        }),
        AddBasicInfo: builder.mutation<IResponseType, FormData>({
            query: userReq => ({
                url: EmployeeEndpointConst.addBasicInfo,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'BasicInfo' }],
        }),
        GetJobInfo: builder.query<IJobInfoResponseType, IRequestType>({
            query: (employeeParams) => getJobInfoByIdUrl(employeeParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'JobInfo' }]
        }),
        AddJobInfo: builder.mutation<IResponseType, IJobInfoType>({
            query: userReq => ({
                url: EmployeeEndpointConst.addJobInfo,
                method: 'POST',
                body: userReq.jobInfo
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'JobInfo' }],
        }),
        GetContactInfo: builder.query<IContactInfoResponseType, IRequestType>({
            query: (employeeParams) => getContactInfoByIdUrl(employeeParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'ContactInfo' }]
        }),
        AddContactInfo: builder.mutation<IResponseType, IContactInfoType>({
            query: userReq => ({
                url: EmployeeEndpointConst.addContactInfo,
                method: 'POST',
                body: userReq.contactInfo
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'ContactInfo' }],
        }),
        GetSecurityInfo: builder.query<ISecurityInfoResponseType, IRequestType>({
            query: (employeeParams) => getSecurityInfoByIdUrl(employeeParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'SecurityInfo' }]
        }),
        GetIncapacitateStatus: builder.mutation<ISecurityInfoResponseType, IRequestType>({
            query: employeeParams => ({
                url: getIncapacitateStatusByIdUrl(employeeParams),
                method: 'POST',
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'SecurityInfo' }]
        }),
        AddSecurityInfo: builder.mutation<IResponseType, ISecurityInfoType>({
            query: userReq => ({
                url: EmployeeEndpointConst.addSecurityInfo,
                method: 'POST',
                body: userReq.securityInfo
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'SecurityInfo' }],
        }),
        UpdateEmployeeStatus: builder.mutation<IResponseType, IUpdateEmployeeStatustype>({
            query: userReq => ({
                url: updateEmployeeStatusUrl(userReq),
                method: 'POST'
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'UpdateEmployeeStatus' }],
        }),
        GetEmployeePassword: builder.mutation<IResponseType, IGetEmployeePasswordRequestType>({
            query: userReq => ({
                url: getEmployeePasswordUrl(userReq),
                method: 'POST'
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'SecurityInfo' }],
        }),
        AddDocumentInfo: builder.mutation<IResponseTypes<boolean>, FormData>({
            query: userReq => ({
                url: EmployeeEndpointConst.addDocumentInfo,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'DocumentInfo' }],
        }),
        GetDocumentInfo: builder.query<IDocumentInfoResponseType, IRequestType>({
            query: (employeeParams) => getDocumentInfoByIdUrl(employeeParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'DocumentInfo' }]
        }),
        DeleteDocumentInfo: builder.mutation<IResponseTypes<boolean>, IRemoveDoctype>({
            query: userReq => ({
                url: EmployeeEndpointConst.deleteDocumentInfo,
                method: 'DELETE',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'DocumentInfo' }],
        }),
        CheckEmailExisting: builder.query<IResponseTypes<boolean>, IEmailExistingParams>({
            query: (employeeParams) => checkEmailExistingUrl(employeeParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'CheckEmailExisting' }]
        }),
        CheckEmployeeCodeExisting: builder.query<IResponseTypes<boolean>, IEmployeeCodeParams>({
            query: (employeeParams) => checkEmployeeCodeExistingUrl(employeeParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'CheckEmployeeCodeExisting' }]
        }),
        GetDownloadContractList: builder.query<IDownloadContractResponseType, IDownloadContractParamsType>({
            query: (employeeParams) => downloadContractUrl(employeeParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'GetDownloadContractList' }]
        }),
        DepartureEmployeeList: builder.mutation<IPaginationResponseTypes<IExitEmployeeDataRow[]>, IEmployeeListRequest>({
            query: userReq => ({
                url: EmployeeEndpointConst.departureEmployeeList,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'DepartureEmployeeList' }],
        }),
        DepartureEmployeeStatus: builder.mutation<IResponseTypes<number>, IExitEmployeeInfo>({
            query: userReq => ({
                url: EmployeeEndpointConst.departureEmployeeStatus,
                method: 'POST',
                body: userReq.exitEmployeeInfoObj
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'EmployeeList' }],
        }),
        DepartureEmployeeProcess: builder.mutation<IResponseTypes<number>, IExitemployeeProcessInfo>({
            query: userReq => ({
                url: EmployeeEndpointConst.departureEmployeeProcess,
                method: 'POST',
                body: userReq.exitEmployeeProcessInfoObj
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'DepartureEmployeeList' }],
        }),       
        GetSlackId: builder.mutation<ISlackResponseType, ISlackRequestType>({
            query: employeeParams => ({
                url: getSlackSyncUrl(employeeParams),
                method: 'POST',
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'slackSync' }]
        }), 
        GetNotificationList: builder.mutation<IPaginationResponseTypes<INotificationResponseType>, INotificationListRequestType>({
            query: userReq => ({
                url: EmployeeEndpointConst.getNotificationListUrl,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'Notification' }]
        }), 
        UpdateNotificationStatus: builder.mutation<IResponseTypes<boolean>, IUpdateNotificationStatusRequestType>({
            query: userReq => ({
                url: updateNotificationUrl(userReq),
                method: 'POST',
                body: userReq.notificationIds
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'Notification' }]
        }), 
        GetExitNotes: builder.query<IResponseTypes<IExitNotesResponseType>, IEmployeeCodeParams>({
            query: (employeeParams) => getExitNotesUrl(employeeParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'ExitNotes' }]
        }),
        AddBankInfo: builder.mutation<IResponseType, IBankInfoType>({
            query: userReq => ({
                url: EmployeeEndpointConst.addBankInfo,
                method: 'POST',
                body: userReq.bankInfo
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'ContactInfo' }],
        }),
        AddTANInfo: builder.mutation<IResponseType, ITANInfoType>({
            query: userReq => ({
                url: EmployeeEndpointConst.addTANInfo,
                method: 'POST',
                body: userReq.tanInfo
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'ContactInfo' }],
        }),
        GetBankInfo: builder.query<IBankInfoResponseType, IBankInfoRequestType>({
            query: (employeeParams) => getBankInfoByIdUrl(employeeParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'ContactInfo' }]
        }),
        GetTANInfo: builder.query<ITANInfoResponseType, IBankInfoRequestType>({
            query: (employeeParams) => getTANInfoByIdUrl(employeeParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Employee', id: 'GetTANInfo' }]
        }),
        UpdateContractStatus: builder.mutation<IResponseType, IUpdateContractStatusInfo>({
            query: userReq => ({
                url: EmployeeEndpointConst.updateContractStatus,
                method: 'POST',
                body: userReq.updateContractStatusInfo
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Employee', id: 'UpdateContractStatus' }],
        }),
    }),
})